import {
  Badge,
  DropdownMenu,
  Icon,
  IconButton,
  Toggle,
  Tooltip,
} from '@kontentino/ui';
import { faArrowDownRight } from '@fortawesome/pro-regular-svg-icons/faArrowDownRight';
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons/faArrowDownToLine';
import { faArrowUpRight } from '@fortawesome/pro-regular-svg-icons/faArrowUpRight';
import { faBarsSort } from '@fortawesome/pro-regular-svg-icons/faBarsSort';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons/faCheckCircle';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faDatabase } from '@fortawesome/pro-regular-svg-icons/faDatabase';
import { faDollarCircle } from '@fortawesome/pro-regular-svg-icons/faDollarCircle';
import { faSliders } from '@fortawesome/pro-regular-svg-icons/faSliders';
import { faFeather } from '@fortawesome/pro-regular-svg-icons/faFeather';
import { faFilePdf } from '@fortawesome/pro-regular-svg-icons/faFilePdf';
import { faImages } from '@fortawesome/pro-regular-svg-icons/faImages';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons/faShareNodes';
import { faTags } from '@fortawesome/pro-regular-svg-icons/faTags';
import { faText } from '@fortawesome/pro-regular-svg-icons/faText';
import { PostExportVariant } from 'constants/post';
import { OrderDirection } from 'app/types';
import React, { FC, useEffect, useState } from 'react';
import { UserPermission } from 'constants/userPermission';
import UserPermissionGate from 'components/UserPermissionGate';
import { useTranslation } from 'react-i18next';
import CsvImportModal from './CsvImportModal';
import { usePostsLayoutState } from '../hooks/usePostsLayoutState';
import { usePageAutoScheduleMutation } from '../hooks/usePageAutoSchedule';
import { preventDefault } from 'utils/mouseEvent';
import { usePostsContainer } from 'components/posts/context';
import { useUser } from 'modules/user/userSelector';
import { faArrowUpFromBracket } from '@fortawesome/pro-regular-svg-icons/faArrowUpFromBracket';
import { faArrowUpToLine } from '@fortawesome/pro-regular-svg-icons/faArrowUpToLine';
import { faBolt } from '@fortawesome/pro-regular-svg-icons/faBolt';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
// [POST-WORKFLOW GATE — TEMPORARY] gates the auto-schedule toggle; remove after full cutover.
import { isPostWorkflowApiNew } from 'app/config/postWorkflowApiMode';

const EXPORT_POSTS_OPTIONS = [
  {
    element: (label: string) => (
      <>
        <Icon icon={faArrowUpFromBracket} />
        {label}
      </>
    ),
    i18nKey: 'externalLink',
    key: PostExportVariant.ExternalLink,
    permission: UserPermission.POST_MASS_EXPORT_MULTIPREVIEW,
    isDisabled: (selectedPosts: number[]) =>
      selectedPosts.length === 0 ? 'noPostsSelectedToShare' : undefined,
  },
  {
    element: (label: string) => (
      <>
        <Icon icon={faFilePdf} />
        {label}
      </>
    ),
    i18nKey: 'PDF',
    key: PostExportVariant.Pdf,
    permission: UserPermission.POST_MASS_EXPORT,
  },
  {
    element: (label: string) => (
      <>
        <Icon icon={faDatabase} />
        {label}
      </>
    ),
    i18nKey: 'CSV',
    key: PostExportVariant.Csv,
    permission: UserPermission.POST_MASS_EXPORT,
  },
  {
    element: (label: string) => (
      <>
        <Icon icon={faText} />
        {label}
      </>
    ),
    i18nKey: 'textOnly',
    key: PostExportVariant.Txt,
    permission: UserPermission.POST_MASS_EXPORT,
  },
  {
    element: (label: string) => (
      <>
        <Icon icon={faImages} />
        {label}
      </>
    ),
    i18nKey: 'imagesOnly',
    key: PostExportVariant.Images,
    permission: UserPermission.POST_MASS_EXPORT,
  },
];

const SORT_POSTS_OPTIONS = [
  {
    i18nKey: 'newestToOldest',
    key: OrderDirection.Desc,
    element: (label: string) => (
      <>
        <Icon icon={faArrowDownRight} />
        {label}
      </>
    ),
  },
  {
    i18nKey: 'oldestToNewest',
    key: OrderDirection.Asc,
    element: (label: string) => (
      <>
        <Icon icon={faArrowUpRight} />
        {label}
      </>
    ),
  },
];

type Props = {
  onSortOptionSelect: (option: OrderDirection) => void;
  onExportOptionSelect: (option: PostExportVariant) => void;
  onTogglePreviews: () => void;
  onOpenTrash: () => void;
  selectedSortOption: OrderDirection;
  arePreviewsOn: boolean;
  canTogglePreviews: boolean;
  canToggleInspirationVisibility: boolean;
  canTogglePostsPageVisibility: boolean;
  canToggleBoostBudgetVisibility: boolean;
  canTogglePostsLabelsVisibility: boolean;
  trashCount?: number;
  disabled?: {
    exportPosts?: boolean;
  };
};

const PostsAdditionalActionsDropdown: FC<Props> = ({
  onSortOptionSelect,
  onExportOptionSelect,
  onOpenTrash,
  selectedSortOption,
  arePreviewsOn,
  onTogglePreviews,
  canTogglePreviews,
  canToggleInspirationVisibility,
  canTogglePostsPageVisibility,
  canToggleBoostBudgetVisibility,
  canTogglePostsLabelsVisibility,
  trashCount,
  disabled,
}) => {
  const postsContainer = usePostsContainer();
  const {
    postStatusVariant,
    togglePostStatusVariant,
    isPostsPageVisible,
    togglePostsPageVisibility,
    isPostsLabelsVisible,
    togglePostsLabelsVisibility,
    isBoostBudgetVisible,
    toggleBoostBudgetVisibility,
    isInspirationsVisible,
    toggleInspirationsVisibility,
    isNewPostExperienceActive,
    toggleNewPostExperience,
  } = usePostsLayoutState();
  const { t } = useTranslation();
  const user = useUser();
  const { setAutoSchedule, isUpdating: isUpdatingAutoSchedule } =
    usePageAutoScheduleMutation();

  useEffect(() => {
    if (user.isClient && postStatusVariant === 'task') {
      togglePostStatusVariant();
    }
  }, [user.isClient, postStatusVariant, togglePostStatusVariant]);

  const [isCsvImportModalOpen, setIsCsvImportModalOpen] = useState(false);

  const noProfilesSelected =
    postsContainer.filterState.selectedProfiles.length === 0 &&
    postsContainer.filterState.selectedGroups.length === 0;

  const selectedProfile =
    postsContainer.filterState.selectedProfiles.length === 1
      ? postsContainer.activeState.profiles.find(
          (p) => p.id === postsContainer.filterState.selectedProfiles[0],
        )
      : null;

  const canImportCsv =
    selectedProfile !== null &&
    postsContainer.filterState.selectedGroups.length === 0;

  const singleProfileSelected =
    selectedProfile != null &&
    postsContainer.filterState.selectedGroups.length === 0;

  // [POST-WORKFLOW GATE — TEMPORARY] Auto-schedule-on-approval is a new-workflow-only feature and
  // its endpoint is gated to allowlisted accounts, so hide the toggle entirely for everyone else
  // rather than offering a control the API would reject. Remove after full cutover.
  const canManageAutoSchedule =
    isPostWorkflowApiNew() &&
    (user.role === 'admin' || user.role === 'manager');

  const isAutoScheduleDisabled =
    user.role !== 'admin' || !singleProfileSelected || isUpdatingAutoSchedule;

  const getAutoScheduleDisabledReason = () => {
    if (!singleProfileSelected) {
      return t('layoutFilter.autoSchedule.selectSingleProfile');
    }

    if (user.role !== 'admin') {
      return t('approvalEmail.autoSchedule.adminOnly');
    }

    return undefined;
  };

  const autoScheduleDisabledReason = getAutoScheduleDisabledReason();

  const handleToggleAutoSchedule = () => {
    if (!selectedProfile) return;

    setAutoSchedule({
      pageId: selectedProfile.id,
      enabled: !selectedProfile.isAutoschedule,
    });
  };

  const autoScheduleMenuItem = (
    <DropdownMenu.Item
      data-name="auto-schedule-toggle"
      data-cy="calendar_auto-schedule-toggle"
      disabled={isAutoScheduleDisabled}
      {...(isAutoScheduleDisabled
        ? {}
        : { onSelect: preventDefault(handleToggleAutoSchedule) })}
    >
      <Icon icon={faBolt} />
      {t('layoutFilter.autoSchedule.title')}
      <Toggle
        onChange={() => {}}
        size="small"
        className="tw-ml-auto"
        disabled={isAutoScheduleDisabled}
        checked={!!selectedProfile?.isAutoschedule}
      />
    </DropdownMenu.Item>
  );

  const handleImportCsv = () => {
    if (!selectedProfile) return;
    setIsCsvImportModalOpen(true);
  };

  const isPageVisibilityDisabled =
    !canTogglePostsPageVisibility ||
    (postsContainer.filterState.selectedProfiles.length === 1 &&
      postsContainer.filterState.selectedGroups.length === 0);

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger disabled={noProfilesSelected}>
          <IconButton
            size={'medium'}
            disabled={noProfilesSelected}
            className="tw-hidden tw-h-[36px] sm:tw-flex"
            variant="secondary"
            data-name="topbar-more-button"
            icon={<Icon icon={faSliders} />}
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content className="tw-z-10">
          <UserPermissionGate scopes={UserPermission.POST_CREATE}>
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger disabled={!canImportCsv}>
                <DropdownMenu.Item
                  data-name="posts-import"
                  data-cy="posts-import"
                >
                  <Icon icon={faArrowUpToLine} />
                  {t('importPosts', 'Import posts')}
                  <Icon
                    size="sm"
                    className="tw-ml-auto"
                    icon={faChevronRight}
                  />
                </DropdownMenu.Item>
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <DropdownMenu.SubContent className="tw-z-10">
                  <DropdownMenu.Item
                    disabled={!canImportCsv}
                    data-name="posts-import-csv"
                    data-cy="posts-import-csv"
                    onClick={handleImportCsv}
                  >
                    <Icon icon={faDatabase} />
                    {t('CSV', 'CSV')}
                  </DropdownMenu.Item>
                </DropdownMenu.SubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
          </UserPermissionGate>
          <UserPermissionGate scopes={UserPermission.POST_MASS_EXPORT}>
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger disabled={disabled?.exportPosts}>
                <DropdownMenu.Item
                  data-name="posts-export"
                  data-cy="posts-export"
                >
                  <Icon icon={faArrowDownToLine} />
                  {t('exportPosts', 'Export posts')}
                  <Icon
                    size="sm"
                    className="tw-ml-auto"
                    icon={faChevronRight}
                  />
                </DropdownMenu.Item>
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <DropdownMenu.SubContent className="tw-z-10">
                  {EXPORT_POSTS_OPTIONS.map((exportOption) => {
                    const disabledText = exportOption.isDisabled?.(
                      postsContainer.selectedPosts,
                    );

                    const item = (
                      <DropdownMenu.Item
                        disabled={!!disabledText}
                        data-name={`posts-export-${exportOption.key}`}
                        data-cy={`posts-export-${exportOption.key}`}
                        key={exportOption.key}
                        onClick={() => onExportOptionSelect(exportOption.key)}
                      >
                        {exportOption.element(t(exportOption.i18nKey))}
                      </DropdownMenu.Item>
                    );

                    return (
                      <UserPermissionGate
                        key={exportOption.key}
                        scopes={exportOption.permission}
                      >
                        {disabledText ? (
                          <Tooltip content={t(disabledText)}>
                            <div>{item}</div>
                          </Tooltip>
                        ) : (
                          item
                        )}
                      </UserPermissionGate>
                    );
                  })}
                </DropdownMenu.SubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
          </UserPermissionGate>
          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger>
              <DropdownMenu.Item data-name="posts-sort" data-cy="posts-sort">
                <Icon icon={faBarsSort} />
                {t('sortPosts', 'Sort posts')}
                <Icon size="sm" className="tw-ml-auto" icon={faChevronRight} />
              </DropdownMenu.Item>
            </DropdownMenu.SubTrigger>
            <DropdownMenu.Portal>
              <DropdownMenu.SubContent className="tw-z-10">
                {SORT_POSTS_OPTIONS.map((sortOption) => (
                  <DropdownMenu.Item
                    data-name={`posts-sort-${sortOption.key}`}
                    data-cy={`posts-sort-${sortOption.key}`}
                    key={sortOption.key}
                    onClick={() => onSortOptionSelect(sortOption.key)}
                  >
                    {sortOption.element(t(sortOption.i18nKey))}
                    {selectedSortOption === sortOption.key && (
                      <span>
                        <Icon icon={faCheck} className="tw-text-primary-100" />
                      </span>
                    )}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.SubContent>
            </DropdownMenu.Portal>
          </DropdownMenu.Sub>
          <DropdownMenu.Separator />
          <div className="tw-px-2 tw-text-sm tw-font-medium tw-text-grayscale-100">
            {t('style', 'Style')}
          </div>
          <DropdownMenu.Item
            data-name="posts-previews-toggle"
            disabled={!canTogglePreviews}
            onSelect={preventDefault(onTogglePreviews)}
          >
            <Icon icon={faImages} />
            {t('showPreviews', 'Show previews')}
            <Toggle
              onChange={() => {}}
              size="small"
              className="tw-ml-auto"
              checked={arePreviewsOn}
              disabled={!canTogglePreviews}
            />
          </DropdownMenu.Item>
          <DropdownMenu.Item
            data-name="show-profile-toggle"
            onSelect={preventDefault(togglePostsPageVisibility)}
            disabled={isPageVisibilityDisabled}
          >
            <Icon icon={faShareNodes} />
            {t('layoutFilter.showProfile', 'Show profile')}
            <Toggle
              onChange={() => {}}
              size="small"
              className="tw-ml-auto"
              checked={!isPageVisibilityDisabled && isPostsPageVisible}
              disabled={isPageVisibilityDisabled}
            />
          </DropdownMenu.Item>
          <DropdownMenu.Item
            data-name="show-labels-toggle"
            onSelect={preventDefault(togglePostsLabelsVisibility)}
            disabled={!canTogglePostsLabelsVisibility}
          >
            <Icon icon={faTags} />
            {t('layoutFilter.showLabels', 'Show labels')}
            <Toggle
              onChange={() => {}}
              size="small"
              className="tw-ml-auto"
              checked={canTogglePostsLabelsVisibility && isPostsLabelsVisible}
              disabled={!canTogglePostsLabelsVisibility}
            />
          </DropdownMenu.Item>
          <DropdownMenu.Item
            data-name="show-boost-budget-toggle"
            onSelect={preventDefault(toggleBoostBudgetVisibility)}
            disabled={!canToggleBoostBudgetVisibility}
          >
            <Icon icon={faDollarCircle} />
            {t('layoutFilter.showBoostBudget', 'Show boost budget')}
            <Toggle
              onChange={() => {}}
              size="small"
              className="tw-ml-auto"
              checked={canToggleBoostBudgetVisibility && isBoostBudgetVisible}
              disabled={!canToggleBoostBudgetVisibility}
            />
          </DropdownMenu.Item>
          <DropdownMenu.Item
            data-name="show-inspirations-toggle"
            onSelect={preventDefault(toggleInspirationsVisibility)}
            disabled={!canToggleInspirationVisibility}
          >
            <Icon icon={faFeather} />
            {t('layoutFilter.showInspirations', 'Show inspirations')}
            <Toggle
              onChange={() => {}}
              size="small"
              className="tw-ml-auto"
              checked={canToggleInspirationVisibility && isInspirationsVisible}
              disabled={!canToggleInspirationVisibility}
            />
          </DropdownMenu.Item>
          {!user.isClient && (
            <DropdownMenu.Item
              data-name="classic-status-mode-toggle"
              onSelect={preventDefault(togglePostStatusVariant)}
            >
              <Icon icon={faCheckCircle} />
              {t('layoutFilter.classicStatusMode', 'Classic status mode')}
              <Toggle
                onChange={() => {}}
                size="small"
                className="tw-ml-auto"
                checked={postStatusVariant === 'task'}
              />
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Separator />
          <DropdownMenu.Item
            data-name="new-post-experience-toggle"
            data-cy="new-post-experience-toggle"
            onSelect={preventDefault(toggleNewPostExperience)}
          >
            <Icon icon={faSparkles} />
            {t('layoutFilter.newPostExperience', 'New post experience')}
            <Badge>Beta</Badge>
            <Toggle
              onChange={() => {}}
              size="small"
              className="tw-ml-auto"
              checked={isNewPostExperienceActive}
            />
          </DropdownMenu.Item>
          {canManageAutoSchedule && (
            <>
              <DropdownMenu.Separator />
              {isAutoScheduleDisabled ? (
                <Tooltip content={autoScheduleDisabledReason}>
                  <div className="tw-w-full">{autoScheduleMenuItem}</div>
                </Tooltip>
              ) : (
                autoScheduleMenuItem
              )}
            </>
          )}
          <UserPermissionGate scopes={UserPermission.POST_DELETE}>
            <DropdownMenu.Separator />
            <DropdownMenu.Item
              data-name="posts-trash"
              data-cy="posts-trash"
              onClick={onOpenTrash}
            >
              <Icon icon={faTrash} />
              {t('showTrash', 'Show trash')}
              {typeof trashCount === 'number' && trashCount > 0 && (
                <Badge className="tw-ml-auto">
                  {trashCount > 99 ? '99+' : trashCount}
                </Badge>
              )}
            </DropdownMenu.Item>
          </UserPermissionGate>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      {isCsvImportModalOpen && selectedProfile && (
        <CsvImportModal
          onClose={() => setIsCsvImportModalOpen(false)}
          pageId={selectedProfile.id}
          socialNetwork={selectedProfile.type}
        />
      )}
    </>
  );
};

export default PostsAdditionalActionsDropdown;
